import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { IShow } from '../../../shows/core/interfaces/IShow';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { ShowsService } from '../../../shows/core/services/ShowsService';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ShowsStore } from '../../../shows/core/stores/ShowsStore';
import { IShowToReleaseReservations } from '../../../shows/core/interfaces/IShowToReleaseReservations';
import { ReservationsService } from '../../core/services/ReservationsService';
import { StatisticsStore } from '../../../statistics/core/stores/StatisticsStore';
import { StatisticsService } from '../../../statistics/core/services/StatisticsService';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';

@Component({
  selector: 'app-non-taken-reservations-to-release-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    LoaderComponent,
    LocaleDatetimeFormatPipe,
  ],
  templateUrl: './non-taken-reservations-to-release-modal.component.html',
  styleUrl: './non-taken-reservations-to-release-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NonTakenReservationsToReleaseModalComponent extends BaseModalComponent {

  public shows: (IShowToReleaseReservations & { show: IShow; })[] = [];
  public readonly isLoading = signal(false);
  public readonly selectedShows = new Map<number, IShowToReleaseReservations & { show: IShow; }>();

  private readonly _showsService = inject(ShowsService);
  private readonly _reservationsService = inject(ReservationsService);
  private readonly _showsStore = inject(ShowsStore);
  private readonly _statisticsStore = inject(StatisticsStore);
  private readonly _statisticsService = inject(StatisticsService);

  public override open(val?: null): void {
    super.open(val);
    this.loadData();
  }

  public confirmReleaseReservations(btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._reservationsService.releaseReservations(
      Array.from(this.selectedShows.values())
        .map(
          s => ({ showId: s.showId, cineCardReservations: s.isFreeReservationCC })
        )
    )
      .pipe(
        finalize(() => btn.classList.remove('disabled'))
      )
      .subscribe({
        next: () => {
          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'The reservations have been successfully released.'.trans(`reservations-modal/notifications/reservations-released-successfully.Message`)
          );

          this.loadData();
          this._statisticsService.getShowsStatistics().subscribe();
          this._reservationsService.getReservations(this._showsStore.selectedShow().id)
            .subscribe({
              error: ({ error }: ApiErrorResponse) => {
                this.notificationService.pushError(
                  'Fehler'.trans('notifications/error.Title'),
                  error.message.trans(`reservations-modal/notifications/${error.message}.Message`)
                );
              }
            });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`release-reservations-for-shows/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private loadData(): void {
    this.selectedShows.clear();
    this.shows = [];
    this.isLoading.set(true);

    this._showsService.getReleaseReservationsShows()
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: res => {
          res.data.forEach(x => {
            const fShow = this._showsStore.shows().find(s => s.id === x.showId);
            const fStatistics = this._statisticsStore.showsStatistics()
              .find(s => s.showId === x.showId);

            if (fShow && fStatistics?.reservedTicketsCount > 0) {
              const obj = { show: fShow, ...x };

              if (fShow.freeReservation && !x.isNormalReleased) {
                this.shows.push({
                  isFreeReservationCC: false,
                  ...obj
                });
              }

              if (fShow.freeReservationCC) {
                this.shows.push({
                  isFreeReservationCC: true,
                  ...obj
                });
              }
            }
          });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`show-for-non-taken-reservations-to-release/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
